/**
 * Message Store
 *
 * Zustand store for server-driven in-app messages.
 * Fetches from GET /api/messages/active, caches in AsyncStorage.
 * Messages are displayed one at a time (queue model, highest priority first).
 *
 * ─── Dismissal persistence (the "C-prune" model) ─────────────────
 *
 * The user's local dismissals live in BOTH the in-memory `dismissed`
 * Set AND the persisted `MessagesCacheStorage.dismissed` array — they
 * are kept in sync by every method that mutates one side.
 *
 * Without persistence, three failure modes hit users in practice:
 *   - **In-flight loss**: dismiss → POST in flight → user kills app
 *     → POST never lands → server still lists the message active →
 *     it reappears on next launch.
 *   - **Server hiccup**: dismiss → POST returns 503 → reload within
 *     the messages TTL window → cache repopulates the dismissed
 *     message.
 *   - **Cache TTL window**: dismiss → POST 200 → reload within ≤ 1h
 *     → the pre-dismissal cached `GET /messages/active` response still
 *     lists the message → it reappears for up to TTL.
 *
 * With persistence, the in-memory + persisted dismissed set survives
 * reloads regardless of server outcome. Growth is bounded: every
 * successful `fetchActiveMessages` intersects the dismissed set with
 * the new messages' IDs, dropping entries for messages that have left
 * the catalog. Self-cleaning, no TTL math, no unbounded growth.
 *
 * See MSG-006 in `docs/specs/e2e/messages/` for the regression tests
 * that pin this behavior.
 */

import { create } from 'zustand';
import { api } from '../services/api';
import { MessagesCacheStorage } from '../generated/sync/sync-config';
import { createLogger } from '../utils/logger';
import type { InAppMessagePayload } from '../generated/api/http-contracts';

const log = createLogger('MessageStore');

// ── Types ────────────────────────────────────────────────────────

interface MessageState {
  messages: InAppMessagePayload[];
  dismissed: Set<string>;
  loaded: boolean;

  /**
   * Fetch active messages from the server and update cache. Resolves
   * `true` on success, `false` when the fetch failed (state untouched).
   * The freshness-registered refresh callback in App.tsx relies on the
   * return value to decide whether to mark the domain synced.
   */
  fetchActiveMessages: () => Promise<boolean>;
  /** Load from AsyncStorage cache only. */
  loadFromCache: () => Promise<void>;
  /** Dismiss a message (local + server). */
  dismissMessage: (id: string) => Promise<void>;
  /**
   * Reset the in-memory store to its initial state. Used by the
   * `dev.clear-messages` dev intent for E2E tests that need a known-
   * empty messages surface between branches. Does NOT touch the
   * AsyncStorage cache or the freshness state — the dev intent
   * handler in `devReset.ts` owns those wipes alongside this call so
   * one intent maps to one consistent action from the test's point
   * of view.
   *
   * Production code paths must not call this — there's no UX where the
   * client side-effects-wipes its own message state. Logout already
   * triggers `dev.clear-state` which clears everything; tests use
   * this lighter variant when they want to reset JUST messages.
   */
  reset: () => void;
}

// ── Helpers ─────────────────────────────────────────────────────

/**
 * Intersect the dismissed set with the IDs in the current messages
 * list. Returns a new array containing only IDs that still correspond
 * to a known message. Self-cleans dismissals for messages that left
 * the catalog (campaign expired, admin pulled it, etc.) so the
 * persisted list stays bounded without an explicit TTL.
 */
function prunedDismissed(
  dismissed: Set<string>,
  messages: InAppMessagePayload[],
): string[] {
  const known = new Set(messages.map((m) => m.id));
  const pruned: string[] = [];
  for (const id of dismissed) {
    if (known.has(id)) pruned.push(id);
  }
  return pruned;
}

/**
 * Persist the current messages + dismissed slice of state to
 * AsyncStorage. Best-effort: storage failures log a warning but never
 * surface to the user — the in-memory state stays the source of truth
 * for the current session.
 */
async function persistCache(
  messages: InAppMessagePayload[],
  dismissed: Set<string>,
): Promise<void> {
  try {
    await MessagesCacheStorage.save({
      messages,
      dismissed: prunedDismissed(dismissed, messages),
    });
  } catch (err) {
    log.warn('Failed to persist messages cache', { error: err });
  }
}

// ── Selectors ───────────────────────────────────────────────────

/** Highest-priority undismissed message, or null. */
export function selectCurrentMessage(state: MessageState): InAppMessagePayload | null {
  for (const msg of state.messages) {
    if (!state.dismissed.has(msg.id)) return msg;
  }
  return null;
}

// ── Store ────────────────────────────────────────────────────────

export const useMessageStore = create<MessageState>((set, get) => ({
  messages: [],
  dismissed: new Set(),
  loaded: false,

  fetchActiveMessages: async () => {
    const result = await api.getActiveMessages();

    if (!result.ok) {
      log.warn('Failed to fetch active messages', { kind: result.kind, error: result.error });
      return false;
    }

    const messages = result.data.messages as InAppMessagePayload[];

    // Prune dismissed set to the new catalog — drops dismissals for
    // messages that are no longer in the active list. If the server
    // later re-adds a previously-dismissed message (campaign re-enabled,
    // typically via a new id but possible with the same id), that
    // message shows again correctly because its id was pruned out here.
    const prunedDismissedArr = prunedDismissed(get().dismissed, messages);
    const dismissed = new Set(prunedDismissedArr);
    set({ messages, dismissed, loaded: true });

    // Freshness ("when was this last synced?") lives in freshnessManager,
    // not in the cache payload. Save messages AND the pruned dismissed
    // list — both halves of the local view of the catalog.
    await persistCache(messages, dismissed);

    log.debug('Fetched active messages', {
      count: messages.length,
      dismissedCount: prunedDismissedArr.length,
    });
    return true;
  },

  loadFromCache: async () => {
    try {
      const cached = await MessagesCacheStorage.load();
      if (cached?.messages && Array.isArray(cached.messages)) {
        // Hydrate BOTH messages and dismissed. The cached dismissed list
        // is already pruned-to-catalog from when it was last written, so
        // we just hand the set straight to React state.
        const cachedDismissed = Array.isArray(cached.dismissed)
          ? (cached.dismissed as string[])
          : [];
        set({
          messages: cached.messages as InAppMessagePayload[],
          dismissed: new Set(cachedDismissed),
          loaded: true,
        });
        log.debug('Loaded messages from cache', {
          count: (cached.messages as unknown[]).length,
          dismissedCount: cachedDismissed.length,
        });
      }
    } catch (err) {
      log.warn('Failed to load messages from cache', err);
    }
  },

  dismissMessage: async (id: string) => {
    // Immediate local feedback — and immediate persistence, so a
    // kill-then-relaunch right after the tap doesn't lose the user's
    // intent. The server POST below is fire-and-forget; the persisted
    // set is what survives, regardless of POST outcome.
    const dismissed = new Set(get().dismissed);
    dismissed.add(id);
    set({ dismissed });
    await persistCache(get().messages, dismissed);

    // Fire-and-forget server call. Local persistence above means this
    // failing has no user-visible effect — the message stays dismissed
    // locally; the server will eventually drop it from the active list
    // on its own schedule (or we'll dismiss again on next foreground
    // when the user notices it's still around).
    const result = await api.dismissMessage(id);
    if (result.ok) {
      log.info('Message dismissed', { messageId: id });
    } else {
      log.warn('Failed to dismiss message on server', { kind: result.kind, error: result.error });
    }
  },

  reset: () => {
    set({ messages: [], dismissed: new Set(), loaded: false });
  },
}));
